import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Typography,
  makeStyles,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import Add_Loan from "../../customForms/Add_Loan/Add_Loan.jsx";
import Add_Borrower from "../../customForms/Add_Borrower/Add_Borrower.jsx";
import EGEditDialog from "../../../../Pages/Common/ExposureGroup/EGEditDialog";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import { fetchFromSQL } from "./M_ManageEntityForm.helper.js";
import ErrorBoundary from "../../error/ErrorBoundary.jsx";
import CustomFormIds from "../../customForms/customFormIds.js";
import { message } from "../../../../../../Utils/config/messages.js";

const useStyles = makeStyles((theme) => ({
  dialogTitleRoot: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: theme.spacing(1),
  },
  ButtonWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "22px",
    marginRight: "15em",
  },
  button: {
    marginRight: theme.spacing(1),
    marginTop: "6px",
    minWidth: "185px",
  },
  AlertMobile: {
    marginBottom: "30px",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      marginBottom: "30px",
      flexDirection: "column",
      alignItems: "center",
      textAlignLast: "center",
    },
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  successDialog: {
    "& .MuiDialog-paper": {
      minWidth: 420,
      maxWidth: 440,
      borderRadius: theme.spacing(1.5),
    },
  },
  successContent: {
    position: "relative",
    padding: theme.spacing(4, 4, 3),
  },
  successCloseButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: "var(--color-text-secondary)",
    'html[data-mode="dark"] &': {
      color: "#E5E7EB",
    },
    "&:hover": {
      color: "var(--color-text-primary)",
      backgroundColor: "var(--total_row_background)",
    },
  },
  successBadge: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    backgroundColor: "var(--total_row_background)",
    color: "var(--color-primary-main)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.75rem",
    fontWeight: 700,
    margin: "0 auto 16px",
    boxShadow: "0 0 0 4px var(--total_row_boldbackground)",
  },
  successTitle: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: "1.25rem",
    marginBottom: theme.spacing(1),
    color: "var(--color-text-primary)",
  },
  successDescription: {
    textAlign: "center",
    color: "var(--color-text-secondary)",
    marginBottom: theme.spacing(3),
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
  successPrimaryAction: {
    display: "flex",
    justifyContent: "center",
  },
}));

const EntityMenuCustomForm = ({
  entity,
  open,
  onClose,
  formtypeId,
  isEditForm = false,
}) => {
  const classes = useStyles();
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [savedRecordId, setSavedRecordId] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const customFormRef = useRef(null);

  const handleFormSuccess = useCallback((recordId) => {
    setSavedRecordId(recordId);
    setShowSuccessDialog(true);
  }, []);

  const handleCloseSuccessDialog = useCallback(() => {
    setShowSuccessDialog(false);
    setSavedRecordId(null);
    onClose();
  }, [onClose]);

  const handleRedirectToRecord = useCallback(() => {
    if (formtypeId === CustomFormIds.EXPOSURE_GROUP) {
      window.location.href = '/home/e79fd120-30ed-46eb-b4b1-54b43b9d1d14/1999f9f9-e0ec-4eb6-90b5-6ed6fede0245';
      return;
    }
    if (savedRecordId) {
      const route =
        formtypeId === CustomFormIds.ADD_LOAN
          ? `/loan/${savedRecordId}`
          : `/borrower/${savedRecordId}`;
      window.location.href = route;
    }
  }, [savedRecordId, formtypeId]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setApiError(null);
    setFormConfig(null);
    setSavedRecordId(null);
    setShowSuccessDialog(false);

    if (formtypeId === CustomFormIds.EXPOSURE_GROUP) {
      setLoading(false);
      return;
    }

    if (entity?.customFormId && !isEditForm) {
      setLoading(false);
      return;
    }
      fetchFromSQL(entity.recordId)
      .then((config) => {
        setFormConfig(config);
        setLoading(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(message.LOAD_CONFIGURATION_ERROR, err);
        setApiError(message.LOAD_CONFIGURATION_ERROR);
        setLoading(false);
      });
  }, [formtypeId, open]);

  const handleCloseClick = () => {
    setShowCloseWarning(true);
  };

  const cancelClose = () => {
    setShowCloseWarning(false);
  };

  const forceClose = () => {
    setShowCloseWarning(false);
    onClose();
  };

  const confirmClose = async () => {
    forceClose();
  };

  const renderFormByType = () => {
    if (loading) {
      return (
        <div className={classes.loaderContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (apiError) {
      return <Typography color="error">{apiError}</Typography>;
    }

    switch (formtypeId) {
      case CustomFormIds.ADD_LOAN:
        return (
          <Add_Loan
            ref={customFormRef}
            entity={entity}
            onClose={onClose}
            formConfig={formConfig}
            isEdit={isEditForm}
            onSaveSuccess={handleFormSuccess}
          />
        );
      case CustomFormIds.ADD_BORROWER:
        return (
          <Add_Borrower
            ref={customFormRef}
            entity={entity}
            onClose={onClose}
            formConfig={formConfig}
            isEditForm={isEditForm}
            onSaveSuccess={handleFormSuccess}
          />
        );
      case CustomFormIds.EXPOSURE_GROUP:
        return (
          <EGEditDialog
            embedded
            open={open}
            groupId={entity.recordId}
            groupName={entity.name}
            isEdit={isEditForm}
            onClose={onClose}
            onSaved={handleFormSuccess}
          />
        );
      default:
        return <Typography>{message.CUSTOM_FORM_CONFIGURE_ERROR}</Typography>;
    }
  };

  return (
    <>
      <A_SimpleDialog
        title={entity?.name}
        open={open && !showSuccessDialog}
        onClose={handleCloseClick}
        fullWidth
        maxWidth="md"
        height="400px"
        dialogContent={
          <>
            {showCloseWarning && (
              <Alert
                severity="warning"
                className={classes.AlertMobile}
                action={
                  <div className={classes.ButtonWrapper}>
                    <A_Button
                      label="Don't close form"
                      onClick={cancelClose}
                      className={classes.button}
                      color="primary"
                      variant="outlined"
                    />
                    <A_Button
                      label="Close form anyway"
                      color="inherit"
                      onClick={confirmClose}
                      className={classes.button}
                      variant="outlined"
                    />
                  </div>
                }
              >
                <AlertTitle>Warning</AlertTitle>
                <strong>
                  {message.LOSE_PROGRESS}
                </strong>
              </Alert>
            )}
            <ErrorBoundary>{renderFormByType()}</ErrorBoundary>
          </>
        }
      />

      <Dialog
        open={showSuccessDialog}
        onClose={handleCloseSuccessDialog}
        className={classes.successDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent className={classes.successContent}>
          <IconButton
            aria-label="close"
            onClick={handleCloseSuccessDialog}
            className={classes.successCloseButton}
          >
            <CloseIcon />
          </IconButton>
          <div className={classes.successBadge}>✓</div>
          <Typography variant="h6" className={classes.successTitle}>
            {isEditForm ? "Record updated" : "Record created"}
          </Typography>
          <Typography className={classes.successDescription}>
            {formtypeId === CustomFormIds.ADD_LOAN
              ? "Loan"
              : formtypeId === CustomFormIds.EXPOSURE_GROUP
              ? "Exposure Group"
              : "Borrower"}{" "}
            {isEditForm
              ? "has been updated successfully."
              : "has been created successfully."}
          </Typography>
          <div className={classes.successPrimaryAction}>
            <A_Button
              label="Open record"
              color="primary"
              onClick={handleRedirectToRecord}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

EntityMenuCustomForm.propTypes = {
  entity: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formtypeId: PropTypes.string.isRequired,
  isEditForm: PropTypes.bool,
};

export default EntityMenuCustomForm;
